import type { FieldComponentProps, FieldGroup } from '@app/types/form.types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InventoryCategorySelectGeneric } from '../InventoryCategorySelect';
import type { InventoryItem } from '../../types';
import { useInventoryMoneyVisibility } from '../../moneyVisibility';

// Helper to cast component types for TypeScript compatibility
const castComponent = <TValue, Extra extends object = Record<string, unknown>>(
  Component: React.ComponentType<FieldComponentProps<TValue> & Extra>
) => Component as unknown as React.ComponentType<FieldComponentProps<unknown>>;

/** Config-driven field groups for the inventory item create/edit form. */
export const useInventoryFormConfig = (): FieldGroup[] => {
  const { t } = useTranslation();
  const { showCost, showPrices } = useInventoryMoneyVisibility();

  return useMemo(
    () => [
      {
        title: t('inventory-form-section-details'),
        fields: [
          {
            name: 'name',
            label: t('inventory-field-name'),
            type: 'text',
            width: 'half',
            rules: { required: t('inventory-validation-name-required') },
          },
          {
            name: 'code',
            label: t('inventory-field-code'),
            type: 'text',
            width: 'half',
            placeholder: t('inventory-field-code-placeholder'),
          },
          {
            // Free text on purpose (a unit is "piece" for gloves and "ml" for a
            // solution), so the placeholder has to carry the example — without it
            // people type the pack size in here.
            name: 'unit',
            label: t('inventory-field-unit'),
            type: 'text',
            width: 'half',
            placeholder: t('inventory-field-unit-placeholder'),
            rules: { required: t('inventory-validation-unit-required') },
          },
          {
            // No rules: uncategorised is a valid item, so this must never block submit.
            name: 'categoryId',
            label: t('inventory-field-category'),
            type: 'custom',
            width: 'half',
            component: castComponent(InventoryCategorySelectGeneric),
            componentProps: { placeholder: t('inventory-category-select-placeholder') },
          },
          {
            name: 'description',
            label: t('inventory-field-description'),
            type: 'textarea',
            width: 'full',
          },
        ],
      },
      {
        // Without any money fields shown it is not about pricing.
        title: showCost || showPrices ? t('inventory-form-section-type-pricing') : t('inventory-form-section-type'),
        fields: [
          {
            name: 'isConsumable',
            label: t('inventory-field-is-consumable'),
            type: 'toggle',
            width: 'third',
            rules: {
              // Mirror the backend CHECK: an item must have at least one capability.
              validate: (_value: unknown, formValues: Partial<InventoryItem>) =>
                Boolean(formValues.isConsumable || formValues.isSellable || formValues.isRentable) ||
                t('inventory-validation-capability-required'),
            },
          },
          {
            name: 'isSellable',
            label: t('inventory-field-is-sellable'),
            type: 'toggle',
            width: 'third',
          },
          {
            name: 'isRentable',
            label: t('inventory-field-is-rentable'),
            type: 'toggle',
            width: 'third',
          },
          // Half width from here down: these labels are long enough that a third
          // of the drawer ellipsises them ("Reorder thre...", "Default sale ...").
          {
            name: 'reorderThreshold',
            label: t('inventory-field-reorder-threshold'),
            type: 'number',
            width: 'half',
          },
          {
            name: 'defaultCost',
            hidden: !showCost,
            label: t('inventory-field-default-cost'),
            type: 'money',
            width: 'half',
          },
          {
            name: 'defaultSalePrice',
            hidden: !showPrices,
            label: t('inventory-field-default-sale-price'),
            type: 'money',
            width: 'half',
          },
          {
            name: 'defaultRentalRate',
            hidden: !showPrices,
            label: t('inventory-field-default-rental-rate'),
            type: 'money',
            width: 'half',
          },
          {
            name: 'defaultRentalRateUnit',
            hidden: !showPrices,
            label: t('inventory-field-default-rental-rate-unit'),
            type: 'select',
            width: 'half',
            options: [
              { label: t('inventory-rental-rate-unit-day'), value: 'day' },
              { label: t('inventory-rental-rate-unit-week'), value: 'week' },
              { label: t('inventory-rental-rate-unit-month'), value: 'month' },
            ],
          },
        ],
      },
    ],
    [t, showCost, showPrices]
  );
};
